'use client';
import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAppName } from '@/contexts/AppNameContext';
import NotificationBell from '@/components/NotificationBell';
import GlobalSearch from '@/components/GlobalSearch';

interface MobileTopBarProps {
  onMenuOpen: () => void;
}

/**
 * Sticky mobile top bar: menu, app name, search, alerts.
 * Account actions (language, currency, profile, sign out) live in the
 * menu drawer's footer — one home, opened by the hamburger.
 */
export default function MobileTopBar({ onMenuOpen }: MobileTopBarProps) {
  const { appName } = useAppName();

  return (
    <header className="sticky top-0 z-30 flex h-12 items-center justify-between gap-2 px-3 bg-card/95 backdrop-blur-sm border-b border-border/60">
      {/* Left: menu button + logo */}
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={onMenuOpen}
          className="p-2 rounded-lg hover:bg-surface transition-colors flex-shrink-0 -ml-2"
          aria-label="Open menu"
        >
          <Icon name="Bars3Icon" size={20} className="text-foreground" />
        </button>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-semibold text-foreground truncate" suppressHydrationWarning>
            {appName}
          </span>
        </div>
      </div>

      {/* Center: search pill */}
      <div className="flex-1 max-w-[200px] mx-2">
        <GlobalSearch compact />
      </div>

      {/* Right: notifications */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <NotificationBell />
      </div>
    </header>
  );
}
